import { get, isEmpty, some, includes } from 'lodash-es';
import { O as AriaAttr, A as BXF_MOUSE_FOCUS, P as WindowEventMap } from './bxf-utils-3908c3c5.js';
import { K as KeyboardEventKey } from './keyboard-event-key.enum-2f6c4d91.js';
import { B as BxfPositionConstants, F as FocusTrap } from './focus-trap-023cd6d1.js';

class BxfOverlay {
    parentContainer;
    targetElement;
    sourceElement;
    _constants = BxfPositionConstants;
    OVERLAY_CONTAINER = this._constants.STRING.OVERLAY_CONTAINER;
    DIV = this._constants.STRING.DIV;
    constructor(parentContainer = document.body) {
        this.parentContainer = parentContainer;
    }
    constructOverlay(element) {
        this.targetElement = element.firstElementChild;
        this.sourceElement = element.lastElementChild;
        return this;
    }
    appendToParentContainer(element) {
        let overlayContainer;
        if (this.overlayContainerExists()) {
            overlayContainer = this.getOverlayContainer();
            overlayContainer.appendChild(element);
        }
        else {
            overlayContainer = document.createElement(this.DIV);
            overlayContainer.className = this.OVERLAY_CONTAINER;
            overlayContainer.appendChild(element);
            this.parentContainer.appendChild(overlayContainer);
        }
    }
    open(element = this.sourceElement) {
        if (element &&
            get(element, 'parentElement.className') !== this.OVERLAY_CONTAINER) {
            this.appendToParentContainer(element);
            element.removeAttribute('style');
            element.style.visibility = 'visible';
        }
    }
    close(element = this.sourceElement) {
        if (element && element.parentElement) {
            let { parentElement } = element;
            element.removeAttribute('style');
            element.style.visibility = 'hidden';
            element.remove();
            if (parentElement.className === this.OVERLAY_CONTAINER &&
                isEmpty(parentElement.children)) {
                parentElement.remove();
                parentElement = null;
            }
            element = null;
        }
    }
    isOpen(element = this.sourceElement) {
        return !!(element &&
            element.parentElement != null &&
            element.style.top);
    }
    overlayContainerExists() {
        return !isEmpty(document.getElementsByClassName(this.OVERLAY_CONTAINER));
    }
    getOverlayContainer() {
        return document.getElementsByClassName(this.OVERLAY_CONTAINER)[0];
    }
    // For Modals
    backdropContainerExists() {
        const { BACKDROP_CONTAINER } = this._constants.STRING;
        return !isEmpty(document.getElementsByClassName(BACKDROP_CONTAINER));
    }
    getBackdropContainer() {
        const { BACKDROP_CONTAINER } = this._constants.STRING;
        return document.getElementsByClassName(BACKDROP_CONTAINER)[0];
    }
}

const { CENTER_POSITION_MODAL, BACKDROP_CONTAINER, DIV, BACKDROP_SHOW, BACKDROP_SHOW_NESTED, BACKDROP_CENTER, DATA_BXF_ARIA_HIDDEN, STYLE, SCRIPT, NOSCRIPT, } = BxfPositionConstants.STRING;
const customWindow = window;
class BxfOverlayCenter {
    parentContainer;
    _tabHandler;
    _initiallyFocusedElem;
    _backdropContainer;
    _previousFocusTrap;
    constructor(parentContainer = document.body) {
        this.parentContainer = parentContainer;
    }
    /**
     * Mark a child as data-bxf-aria-hidden.
     *
     * data-bxf-aria-hidden used to mark elements that should be hidden
     * 	from assistive technology when a modal is opened, and should not
     * 	be hidden from assistive technology when a modal is closed.
     *
     * 	Items already hidden from assistive technology should not have
     * 	data-bxf-aria-hidden set, as they are hidden regardless of
     * 	whether modal is open.
     */
    setAriaHiddenOnChild(child) {
        if (child.getAttribute(AriaAttr.HIDDEN) !== 'true') {
            child.setAttribute(DATA_BXF_ARIA_HIDDEN, 'true');
            child.setAttribute(AriaAttr.HIDDEN, 'true');
        }
    }
    /**
     * Hide all elements except the modal backdrop
     * @see {@link https://docs.deque.com/issue-help/1.0.0/en/reading-order-browse-outside-modal Deque Docs - Screen readers can read parent page content outside the modal}
     * @param backdropContainer modal backdrop
     */
    initAriaHidden(element) {
        // Ignore script/style elements, as those are already hidden from AT
        const tagsToIgnore = [SCRIPT, STYLE, NOSCRIPT];
        let currentElement = element;
        while (currentElement && currentElement !== document.body) {
            // Get the parent of the current element
            // eslint-disable-next-line prefer-const
            let parentElement = currentElement.parentElement;
            if (parentElement) {
                // Mark all siblings of the current element or parent with aria-hidden="true"
                Array.from(parentElement.children).forEach((child) => {
                    if (child !== currentElement &&
                        !tagsToIgnore.includes(child.tagName.toLowerCase())) {
                        this.setAriaHiddenOnChild(child);
                    }
                });
            }
            // Move up to the parent for the next iteration
            currentElement = parentElement;
        }
    }
    constructBackdropContainer() {
        const CANVAS = 'bxf-chat-canvas';
        const backdropContainer = document.createElement(DIV);
        backdropContainer.className = BACKDROP_CONTAINER;
        this.setBackdropContainerTopInEbc(backdropContainer);
        const overlayPaneCenter = this.appendBackdropCenterElement(this.appendBackDropElement(backdropContainer));
        const chatCanvas = document.querySelector(CANVAS);
        backdropContainer.slot = 'overlay';
        if (chatCanvas) {
            chatCanvas.appendChild(backdropContainer);
        }
        else {
            console.warn(`${CANVAS} not found in the document`);
            document.body.appendChild(backdropContainer);
        }
        this.initAriaHidden(backdropContainer);
        return overlayPaneCenter;
    }
    /**
     * Check if this app is opened in electron base container.
     * Find if controls overlay height(i.e. app header height) is defined on window.
     * If height is defined, set backdrop container top to start below EBC header.
     */
    setBackdropContainerTopInEbc(backdropContainer) {
        const ebcAppManifest = window?.ebc?.appManifest;
        if (ebcAppManifest && ebcAppManifest.windowControlsOverlay) {
            const { controlsOverlayHeight } = ebcAppManifest;
            if (controlsOverlayHeight) {
                backdropContainer.style.top = controlsOverlayHeight;
            }
        }
    }
    appendToParentContainer(element) {
        const overlay = new BxfOverlay(this.parentContainer);
        const backdropContainer = overlay.getBackdropContainer();
        let backdropPaneCenter;
        if (overlay.backdropContainerExists() &&
            !this.checkIfElementExists(element)) {
            backdropPaneCenter = this.appendBackdropCenterElement(this.appendBackDropElement(backdropContainer));
        }
        else if (!overlay.backdropContainerExists()) {
            backdropPaneCenter = this.constructBackdropContainer();
        }
        if (backdropPaneCenter) {
            backdropPaneCenter.appendChild(element);
            element.tabIndex = -1;
            element.focus();
        }
    }
    /**
     * Check if same element already exists in backdrop or not.
     * In case of a header change,should return true and should update the existing element.
     * In case of nested modals, should return false and create a new modal.
     * @param element
     * @returns {boolean}
     */
    checkIfElementExists(element) {
        // Parent of modal div
        const modals = document.querySelectorAll(CENTER_POSITION_MODAL);
        return some(modals, (node) => node.isSameNode(element));
    }
    /**
     * Set initially focused element to return focus to
     * 	when modal is closed
     */
    setInitiallyFocusedElem() {
        this._initiallyFocusedElem = FocusTrap.getFocusedElem('document');
    }
    /**
     * Get initially focused element and determine whether or not to further
     *  apply focus ring.
     * @returns {boolean}
     */
    setMouseFocusForInitialElem() {
        this.setInitiallyFocusedElem();
        return this._initiallyFocusedElem.classList.contains(BXF_MOUSE_FOCUS);
    }
    /**
     * Open overlay
     */
    open(element) {
        this.appendToParentContainer(element);
        // DO NOT MOVE: This function should always be called after `appendToParentContainer()`.
        this.attachKeyboardListener(element);
    }
    /**
     * Add keyboard event listener to controller
     */
    attachKeyboardListener(element) {
        this._tabHandler = (e) => this.tabHandler(e, element);
        document.addEventListener(WindowEventMap.keydown, this._tabHandler);
        this._previousFocusTrap = customWindow.activeFocusTrap;
        customWindow.activeFocusTrap = element;
    }
    /**
     * Listen for tab key event
     */
    tabHandler(event, element) {
        if (event.key === KeyboardEventKey.Tab) {
            FocusTrap.trapFocus(event, element);
        }
    }
    /**
     * Remove keyboard listener for controller
     */
    removeKeyboardListener() {
        document.removeEventListener(WindowEventMap.keydown, this._tabHandler);
        customWindow.activeFocusTrap = this._previousFocusTrap;
    }
    removeChildElements(element, parentElement) {
        while (element && !includes(element.classList, BACKDROP_SHOW)) {
            parentElement?.removeChild(element);
            if (parentElement !== this.parentContainer) {
                element = parentElement;
                parentElement = element?.parentElement;
            }
            else {
                break;
            }
        }
        return element;
    }
    /**
     * Remove data-bxf-aria-hidden from a document body sibling or from nested modal
     * @param elem element to remove data-bxf-aria-hidden from
     */
    removeBxfAriaHiddenFromElem(elem) {
        elem.removeAttribute(DATA_BXF_ARIA_HIDDEN);
        elem.removeAttribute(AriaAttr.HIDDEN);
    }
    /**
     * Remove aria-hidden attribute from all sibling elements
     * 	on modal close, excluding any elements that had aria-hidden prior to opening modal
     * @see {@link https://docs.deque.com/issue-help/1.0.0/en/reading-order-browse-outside-modal Deque Docs - Screen readers can read parent page content outside the modal}
     */
    removeAriaHiddenFromBodyElements() {
        const hiddenElems = Array.from(document.body?.querySelectorAll(`[${DATA_BXF_ARIA_HIDDEN}]`));
        hiddenElems.forEach((elem) => {
            this.removeBxfAriaHiddenFromElem(elem);
        });
    }
    /**
     * Close overlay and return focus to initially focused element,
     * only displaying focus ring if overlay was triggered via keyboard.
     */
    close(element, isMouseFocus = false) {
        this.removeKeyboardListener();
        if (element && element.parentElement) {
            const elem = this.removeChildElements(element, element.parentElement);
            const parentElement = elem?.parentElement;
            if (parentElement) {
                elem && elem.remove();
                if (includes(parentElement.classList, BACKDROP_CONTAINER) &&
                    isEmpty(parentElement.children) &&
                    this.parentContainer.contains(parentElement)) {
                    this.removeAriaHiddenFromBodyElements();
                    parentElement.remove();
                }
            }
            this.applyDarkOverlayToTopmostModal();
        }
        if (this._initiallyFocusedElem) {
            FocusTrap.toggleMouseFocus(this._initiallyFocusedElem, isMouseFocus);
            this._initiallyFocusedElem.focus();
        }
    }
    setCustomZIndex(zIndex) {
        const elements = document.querySelectorAll(`.${BACKDROP_CONTAINER}, .${BACKDROP_SHOW}, .${BACKDROP_CENTER}`);
        elements.forEach((element) => {
            element.style.zIndex = zIndex?.toString();
        });
    }
    /**
     * After removing the top-most modal in the nested case,
     * ensure the dark overlay gets moved to the modal underneath
     */
    applyDarkOverlayToTopmostModal() {
        const backdropContainer = document.querySelector(`.${BACKDROP_CONTAINER}`);
        const topModal = backdropContainer?.children[backdropContainer.children.length - 1];
        if (topModal) {
            topModal.classList.remove(BACKDROP_SHOW_NESTED);
            this.removeBxfAriaHiddenFromElem(topModal);
            topModal.classList.add(BACKDROP_SHOW);
        }
    }
    appendBackDropElement(backdropContainer) {
        const overlayBackground = document.createElement(DIV);
        const overlayWithGrayColor = document.querySelector(`.${BACKDROP_SHOW}`);
        /**
         * When adding a new modal, remove the gray overlay color from
         * the previous top-most modal to prevent duplicating the gray background
         * and darkening the screen each time a modal is added to the DOM
         */
        if (overlayWithGrayColor) {
            overlayWithGrayColor.classList.remove(BACKDROP_SHOW);
            overlayWithGrayColor.classList.add(BACKDROP_SHOW_NESTED);
            // add aria-hidden to the nested modal
            this.setAriaHiddenOnChild(overlayWithGrayColor);
        }
        /**
         * if an overlay with the gray backdrop already exists, use
         * an overlay with transparent color instead
         */
        overlayBackground.className = BACKDROP_SHOW;
        backdropContainer.appendChild(overlayBackground);
        return overlayBackground;
    }
    appendBackdropCenterElement(overlayBackground) {
        const overlayPaneCenter = document.createElement(DIV);
        overlayPaneCenter.className = BACKDROP_CENTER;
        overlayBackground.appendChild(overlayPaneCenter);
        return overlayPaneCenter;
    }
    backDropElementExists() {
        return !isEmpty(document.querySelectorAll(`.${BACKDROP_SHOW}, .${BACKDROP_SHOW_NESTED}`));
    }
    /**
     * Sets the width of the overlay backdrop container/lightbox
     * Width parameter must be a valid css width value
     */
    setWidth(width) {
        if (this._backdropContainer) {
            this._backdropContainer.style.width = width;
        }
    }
}

const BxfModalConstants = {
    CONTAINER_STICKY: 'bxf-app-frame__container--sticky',
    BXF_MODAL: 'bxf-modal',
    CONTAINER: 'bxf-modal__container',
    CONTAINER_LARGE: 'bxf-modal__container--large',
    HEADER: 'bxf-modal__header',
    HEADER_LARGE: 'bxf-modal__header--large',
    HEADER_COMPACT: 'bxf-modal__header--compact',
    HEADER_CLOSE_ICON: 'bxf-modal__header-close-icon',
    HEADER_TEXT: 'bxf-modal__header-text',
    HEADER_TEXT_COMPACT: 'bxf-modal__header-text--compact',
    WRAPPER: 'bxf-modal__wrapper',
    WRAPPER_NO_BORDER: 'bxf-modal__wrapper--no-border',
    CONTENT: 'bxf-modal__content',
    CONTENT_LARGE: 'bxf-modal__content--large',
    CONTENT_GREY: 'bxf-modal__content--grey',
    CONTENT_PADDING: 'bxf-modal__content--padding',
    CONTAINER_INFORMATION: 'bxf-modal__container--information',
    CONTAINER_ACTION: 'bxf-modal__container--action',
    BUTTONS: 'bxf-modal__buttons',
    PRIMARY_BUTTON_PADDING: 'bxf-modal__primary-button--padding',
    PRIMARY_BUTTON_PADDING_COMPACT: 'bxf-modal__primary-button-padding--compact',
    CONTENT_HIDE: 'bxf-hidden',
    FOOTER: 'bxf-modal__footer',
    FOOTER_COMPACT: 'bxf-modal__footer--compact',
    LARGE_FOOTER: 'bxf-modal__footer--large',
    PATH: 'path',
    CLASSNAME: 'className',
    ACTION: 'action',
    INFORMATION: 'information',
    CLOSE_BUTTON_LABEL: 'Close',
    SUBMIT: 'Submit',
    CANCEL: 'Cancel',
    CUSTOM_FOOTER_BUTTON: 'bxf-modal__footer-buttons',
    PRIMARY_BUTTON: 'primary-button',
    SECONDARY_BUTTON: 'secondary-button',
    TERTIARY_BUTTON: 'tertiary-button',
    CUSTOM_FOOTER: 'custom-footer',
    CUSTOM_FOOTER_SLOT: "[slot='custom-footer']",
    PRIMARY_BUTTON_SLOT: "[slot='primary-button']",
    SECONDARY_BUTTON_SLOT: "[slot='secondary-button']",
    TERTIARY_BUTTON_SLOT: "[slot='tertiary-button']",
};

export { BxfModalConstants as B, BxfOverlay as a, BxfOverlayCenter as b };
